import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  Plus,
  Trash2,
  Loader2,
  CalendarClock,
  RefreshCw,
  Download,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { feedWatchService } from '../../services/feedWatch';
import type { Schedule, ScheduleFrequency } from '../../services/feedWatch';

const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'custom_cron', label: 'Custom Cron' },
];

function FrequencyBadge({ frequency }: { frequency: ScheduleFrequency }) {
  const colors: Record<ScheduleFrequency, string> = {
    hourly: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    daily: 'bg-green-500/15 text-green-400 border-green-500/30',
    weekly: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
    custom_cron: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  };
  const labels: Record<ScheduleFrequency, string> = {
    hourly: 'Hourly',
    daily: 'Daily',
    weekly: 'Weekly',
    custom_cron: 'Custom',
  };
  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${colors[frequency]}`}>
      {labels[frequency]}
    </span>
  );
}

function formatDate(date: string | null) {
  if (!date) return '—';
  return new Date(date).toLocaleString();
}

interface ScheduleManagerProps {
  feedId: string;
}

export function ScheduleManager({ feedId }: ScheduleManagerProps) {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Create form state
  const [frequency, setFrequency] = useState<ScheduleFrequency>('daily');
  const [cronExpr, setCronExpr] = useState('');
  const [autoExport, setAutoExport] = useState(false);

  const loadSchedules = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await feedWatchService.listSchedules(feedId);
      setSchedules(Array.isArray(result) ? result : []);
    } catch {
      // handled
    } finally {
      setIsLoading(false);
    }
  }, [feedId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const handleCreate = async () => {
    try {
      setIsCreating(true);
      const schedule = await feedWatchService.createSchedule(feedId, {
        frequency,
        cron_expr: frequency === 'custom_cron' ? cronExpr : undefined,
        auto_export: autoExport,
      });
      setSchedules((prev) => [...prev, schedule]);
      setFrequency('daily');
      setCronExpr('');
      setAutoExport(false);
    } catch {
      // handled
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggle = async (schedule: Schedule) => {
    try {
      setTogglingId(schedule.id);
      const updated = await feedWatchService.updateSchedule(feedId, schedule.id, {
        is_active: !schedule.is_active,
      });
      setSchedules((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch {
      // handled
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (scheduleId: string) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      setDeletingId(scheduleId);
      await feedWatchService.deleteSchedule(feedId, scheduleId);
      setSchedules((prev) => prev.filter((s) => s.id !== scheduleId));
    } catch {
      // handled
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Create schedule */}
      <Card className="border-2 shadow-lg rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5" />
          New Schedule
        </h3>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as ScheduleFrequency)}
              className="flex h-10 rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {frequency === 'custom_cron' && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Cron Expression</label>
              <Input
                value={cronExpr}
                onChange={(e) => setCronExpr(e.target.value)}
                placeholder="*/30 * * * *"
                className="w-48 h-10 rounded-lg"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Auto Export</label>
            <button
              onClick={() => setAutoExport(!autoExport)}
              className={`
                flex items-center gap-2 h-10 px-4 rounded-lg border text-sm font-medium transition-all duration-200
                ${autoExport
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-background border-input text-muted-foreground hover:text-foreground'
                }
              `}
            >
              <Download className="h-4 w-4" />
              {autoExport ? 'On' : 'Off'}
            </button>
          </div>

          <Button
            onClick={handleCreate}
            disabled={isCreating || (frequency === 'custom_cron' && !cronExpr.trim())}
            className="rounded-lg h-10"
          >
            {isCreating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Schedule
          </Button>
        </div>
      </Card>

      {/* Schedule list */}
      {schedules.length === 0 ? (
        <Card className="border-2 border-dashed rounded-xl p-12 text-center">
          <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-medium text-muted-foreground">No schedules yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create a schedule to automate feed refreshes.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {schedules.map((schedule) => (
            <Card key={schedule.id} className="border-2 shadow-lg rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <FrequencyBadge frequency={schedule.frequency} />

                  {/* Active toggle */}
                  <button
                    onClick={() => handleToggle(schedule)}
                    disabled={togglingId === schedule.id}
                    className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
                      ${schedule.is_active ? 'bg-primary' : 'bg-muted-foreground/30'}
                    `}
                  >
                    <span
                      className={`
                        inline-block h-4 w-4 rounded-full bg-white transition-transform duration-200
                        ${schedule.is_active ? 'translate-x-6' : 'translate-x-1'}
                      `}
                    />
                  </button>
                  <span className="text-sm text-muted-foreground">
                    {schedule.is_active ? 'Active' : 'Paused'}
                  </span>

                  {schedule.auto_export && (
                    <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                      <Download className="h-3 w-3" />
                      Auto Export
                    </span>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleDelete(schedule.id)}
                  disabled={deletingId === schedule.id}
                >
                  {deletingId === schedule.id
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Trash2 className="h-4 w-4" />
                  }
                </Button>
              </div>

              <div className="flex gap-6 mt-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5" />
                  Next: {formatDate(schedule.next_run_at)}
                </div>
                <div className="flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Last: {formatDate(schedule.last_run_at)}
                </div>
                {schedule.cron_expr && (
                  <div className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                    {schedule.cron_expr}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
